<script setup lang="ts">
import '@aab/sc-aab-icon';
import { DOWN_ARROW_KEY_CODE, ENTER_KEY_CODE, ESCAPE_KEY_CODE, SPACE_BAR_KEY_CODE } from './constants/KeyCodes';
import type { DropDownOption } from './models/DropDownOption';
import { reactive } from 'vue';
import { sy_arrow_chevron_down as syArrowChevronDown } from '@aab/sc-aab-icon-set';

const emit = defineEmits(['selectionChanged']);

const props = withDefaults(
  defineProps<{
    readonly options: ReadonlyArray<DropDownOption>;
    readonly extendedOptions: ReadonlyArray<DropDownOption>;
    readonly defaultOption: DropDownOption['value'];
    readonly label: string;
    readonly id: string;
  }>(),
  { label: '' }
);

const state = reactive({
  displayedOptions: [...props.options],
  selectedOption: props.options.find((item) => item.value === props.defaultOption),
  isDropdownOpen: false
});

function toggleListVisibility(event: KeyboardEvent): void | never {
  if (event.keyCode === ESCAPE_KEY_CODE) return closeList();

  if (event.keyCode === DOWN_ARROW_KEY_CODE) {
    // avoid default effect
    event.preventDefault();
    state.isDropdownOpen = true;
    if (!state.selectedOption) return;
    const selector = getOptionId(props.id, state.selectedOption.value);
    const option: HTMLElement | null = document.querySelector(`#${selector}`);
    if (!option) throw new Error(`Cannot toggle visibility of selectedOption "${selector}"`);
    option.focus();
    return;
  }

  const isDropDownOpen = SPACE_BAR_KEY_CODE.includes(event.keyCode) || event.keyCode === ENTER_KEY_CODE;
  if (isDropDownOpen) {
    // avoid space key to do unnecessary scroll
    if (SPACE_BAR_KEY_CODE.includes(event.keyCode)) event.preventDefault();

    state.isDropdownOpen = !state.isDropdownOpen;
    if (state.isDropdownOpen) {
      if (!state.selectedOption) return;
      const selector = getOptionId(props.id, state.selectedOption.value);
      const option: HTMLElement | null = document.querySelector(`#${selector}`);
      if (!option) throw new Error(`Cannot toggle visibility of selectedOption "${selector}"`);
      option.focus();
    }
    return;
  }
  if (event.type === 'click') {
    if (state.isDropdownOpen) {
      closeList();
    } else {
      state.isDropdownOpen = true;
    }
  }
}

function getOptionId(id: string, value: string): string {
  return `item__${id}__${value}`;
}

function closeList(): void | never {
  state.isDropdownOpen = false;
  if (state.selectedOption?.value) state.displayedOptions = [...props.options];

  const list: HTMLElement | null = document.getElementById(props.id);
  if (!list) throw new Error(`Cannot close list "${props.id}"`);
  list.focus();
}

function setOption(option: DropDownOption): void {
  if (option.value !== 'more-types') {
    state.selectedOption = option;
    emit('selectionChanged', { detail: { value: option.value } });
    closeList();
  } else {
    state.displayedOptions = [...props.extendedOptions];
  }
}

function isSelectedOption(value: DropDownOption['value']): boolean {
  return value === state.selectedOption?.value;
}

function getSelectedValue(): string | undefined {
  return props.extendedOptions.find((option) => option.value === state.selectedOption?.value)?.name;
}

function focusNextListItem(currentActiveElementIndex: number, option: DropDownOption): void {
  const isCurrentActiveElementIsNotLastItem = currentActiveElementIndex < state.displayedOptions.length - 1;
  if (isCurrentActiveElementIsNotLastItem) {
    const nextOption = state.displayedOptions[state.displayedOptions.findIndex((o) => o.value === option.value) + 1];
    const nextOptionSelector = getOptionId(props.id, nextOption.value);
    const item: HTMLElement | null = document.querySelector(`#${nextOptionSelector}`);
    if (!item) throw new Error(`Cannot focus next item "${nextOptionSelector}"`);
    item.focus();
  }
}

function focusPreviousListItem(currentActiveElementIndex: number, option: DropDownOption): void {
  const isCurrentActiveElementIsNotLastItem = currentActiveElementIndex > 0;
  if (isCurrentActiveElementIsNotLastItem) {
    const nextOption = state.displayedOptions[state.displayedOptions.findIndex((o) => o.value === option.value) - 1];
    const nextOptionSelector = getOptionId(props.id, nextOption.value);
    const item: HTMLElement | null = document.querySelector(`#${nextOptionSelector}`);
    if (!item) throw new Error(`Cannot focus next item "${nextOptionSelector}"`);
    item.focus();
  }
}

function navListItems(index: number): void {
  if (index === state.displayedOptions.length - 1) closeList();
}

function focusSelectedItem(event: Event): void {
  if (state.isDropdownOpen) {
    event.preventDefault();
    if (!state.selectedOption) return;
    const selector = getOptionId(props.id, state.selectedOption.value);
    const option: HTMLElement | null = document.querySelector(`#${selector}`);
    if (!option) throw new Error(`Cannot focus selectedItem "${selector}"`);
    option.focus();
  }
}
</script>

<template>
  <div class="drop-down">
    <div class="em-dropdown">
      <div
        :id="id"
        role="button"
        aria-haspopup="listbox"
        :aria-label="label"
        :aria-expanded="state.isDropdownOpen"
        class="em-dropdown__selected"
        :class="{ expanded: state.isDropdownOpen }"
        tabindex="0"
        @click="toggleListVisibility($event)"
        @keydown="toggleListVisibility($event)"
        @keydown.tab="focusSelectedItem($event)"
      >
        {{ getSelectedValue() }}
        <aab-icon class="em-dropdown__arrow" :class="{ expanded: state.isDropdownOpen }" :svg="syArrowChevronDown" size="3" />
      </div>
      <div class="em-dropdown__list-container">
        <ul class="em-dropdown__list" :class="{ open: state.isDropdownOpen }" role="listbox" :aria-activedescendant="state.selectedOption ? state.selectedOption.value : ''" :aria-label="label">
          <li
            v-for="(item, key) in state.displayedOptions"
            :id="getOptionId(id, item.value)"
            :key="key"
            :ref="'em-dropdown-list-item-' + key"
            :aria-selected="isSelectedOption(item.value)"
            class="em-dropdown__list-item"
            :class="{ 'see-more': item.value === 'more-types', selected: isSelectedOption(item.value) }"
            :tabindex="state.isDropdownOpen ? 0 : -1"
            role="option"
            @click="setOption(item)"
            @keydown.enter="setOption(item)"
            @keydown.space.prevent="setOption(item)"
            @keydown.down.prevent="focusNextListItem(key, item)"
            @keydown.up.prevent="focusPreviousListItem(key, item)"
            @keydown.tab="navListItems(key)"
            @keydown.esc="closeList"
          >
            {{ item.name }}
            <aab-icon v-if="item.value === 'more-types'" class="em-dropdown__arrow" :svg="syArrowChevronDown" size="3" />
          </li>
        </ul>
      </div>
    </div>
    <div class="em-dropdown__outer" :class="{ visible: state.isDropdownOpen }" @click="closeList" />
  </div>
</template>

<style lang="scss" scoped>
@use '@aab/sc-styles-colors/dist/scss/styles' as colors;

.drop-down {
  .em-dropdown {
    list-style: none;
    position: relative;
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;
    width: 100%;
    padding: 0;
    background: colors.$white;
    border: 1px solid colors.$n75;
  }

  .em-dropdown__label {
    cursor: default;
    position: absolute;
    top: -27px;
    left: 0;
  }

  .em-dropdown__arrow {
    position: absolute;
    right: 5px;
    transition: transform 0.2s linear;
  }

  .em-dropdown__arrow.expanded {
    transform: rotate(180deg);
  }

  .em-dropdown__list {
    width: 100%;
    position: absolute;
    left: 0;
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;
    max-height: 0;
    overflow: hidden;
    @media (max-width: 575px) {
      position: relative;
    }
  }

  .em-dropdown__list-container {
    position: relative;
    z-index: 9;
  }

  .em-dropdown ul {
    list-style: none;
    margin: 1px 0;
    padding: 0;
  }

  .em-dropdown__list ul {
    list-style: none;
    margin: 1px 0;
    padding: 0;
  }

  .em-dropdown__list-item {
    cursor: pointer;
    padding: 8px 9px;
    transition: background-color 0.1s linear, color 0.1s linear;
    list-style-position: inside;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background-color: colors.$white;
  }

  .em-dropdown__list-item.selected {
    background-color: colors.$g300;
    color: colors.$white;
  }

  .em-dropdown__list-item:hover,
  .em-dropdown__list-item:focus {
    background-color: colors.$g55;
    color: colors.$n670;
  }

  .see-more {
    border-top: 1px solid colors.$n75;
    background: colors.$gm50;
    color: colors.$g300;
  }

  .em-dropdown__selected {
    cursor: pointer;
    padding: 8px 25px 8px 9px;
    list-style-position: inside;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .open {
    overflow: auto;
    max-height: 250px;
    border: 1px solid #ccc;
    border-top: none;
    box-shadow: 0 8px 16px rgba(34, 34, 34, 0.3), 0 0 8px rgba(34, 34, 34, 0.0224185);
  }

  .em-dropdown__outer {
    background: transparent;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 4;
    display: none;
  }

  .em-dropdown__outer.visible {
    display: block;
  }
}
</style>
